import type { ExpressStockOption, ExpressBlockResolution, DeliveryOptionRow } from 'data-types';
import { ABTestManager } from '../../../../utils/ABTestManager';

const SECONDS_IN_HOUR = 3600;

/**
 * Returns stock codes scoped to the selected express tier.
 * When tier is null, returns the full deduplicated union (backward-compatible).
 */
export const isExpressStateFallback = (expressStockOptions: ExpressStockOption[]): boolean => {
  const { state } = resolveExpressBlockState(expressStockOptions);
  return state === 'hidden' || state === 'noneAvailable' || state === 'sameDayMissed';
};

export const getStockCodesForTier = (
  options: ExpressStockOption[],
  tier: 'uber_2h' | 'same_day' | null,
): string[] => {
  if (!tier) {
    return [...new Set(options.map(o => o.stockCode))];
  }
  return [
    ...new Set(
      options
        .filter(o => o.tiers.some(t =>
          t.tier === tier && t.isAvailable && t.isCutoffPassed !== true
        ))
        .map(o => o.stockCode)
    ),
  ];
};



// HL-10272: when a provider is closed for new sales right now (isExpress<Provider>Flow === false),
// force its option row disabled regardless of ZIP coverage. undefined => provider treated as open
// (backward-compatible). uber_2h = Uber, same_day = Urbify.
export const applyProviderAvailability = (
  option: DeliveryOptionRow | null,
  isProviderOpen: boolean | undefined,
  closedReason: string,
): DeliveryOptionRow | null => {
  if (!option) return option;
  if (isProviderOpen === false && option.isEnabled) {
    return { ...option, isEnabled: false, isDefault: false, disabledReason: closedReason };
  }
  return option;
};

export const expressDefaultTierABTest = new ABTestManager({
  testName: 'express_default_tier',
  variants: ['uber_2h', 'same_day'],
});

export const expressTestVariantLabel = (variant: string): 'A' | 'B' =>
  variant === 'same_day' ? 'B' : 'A';

/**
 * Parse an HH:mm cutoff string into a target timestamp for today in Europe/Berlin.
 * Returns epoch ms, or null if the input is malformed.
 */
const parseCutoffToEpochMs = (cutoffTime: string): number | null => {
  const parts = cutoffTime.split(':');
  if (parts.length !== 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;

  const nowBerlin = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' })
  );
  const cutoffDate = new Date(nowBerlin);
  cutoffDate.setHours(hours, minutes, 0, 0);

  const offsetMs = Date.now() - nowBerlin.getTime();
  return cutoffDate.getTime() + offsetMs;
};

/**
 * Returns positive seconds until cutoff if it's in the future AND within 1 hour.
 * Returns null if cutoff has passed or is more than 1 hour away.
 */
export const getSecondsUntilCutoff = (cutoffTime: string): number | null => {
  const cutoffMs = parseCutoffToEpochMs(cutoffTime);
  if (cutoffMs === null) return null;

  const diffSeconds = Math.floor((cutoffMs - Date.now()) / 1000);
  if (diffSeconds <= 0 || diffSeconds > SECONDS_IN_HOUR) return null;
  return diffSeconds;
};

/**
 * Pure function that resolves the UI state for the express delivery block
 * from the API-returned expressStockOptions.
 */
export const resolveExpressBlockState = (
  expressStockOptions: ExpressStockOption[],
): ExpressBlockResolution => {
  const allTiers = expressStockOptions.flatMap(opt => opt.tiers);

  const uberTier = allTiers.find(t => t.tier === 'uber_2h' && t.isAvailable);
  const allSameDayTiers = allTiers.filter(t => t.tier === 'same_day');
  const sameDayTier = allSameDayTiers.length > 0
    ? allSameDayTiers.reduce((latest, current) => {
        const latestStillAvailable = latest.isCutoffPassed !== true;
        const currentStillAvailable = current.isCutoffPassed !== true;
        if (currentStillAvailable && !latestStillAvailable) return current;
        if (!currentStillAvailable && latestStillAvailable) return latest;
        return (current.cutoffTime ?? '') > (latest.cutoffTime ?? '') ? current : latest;
      })
    : undefined;

  const isUberAvailable = !!uberTier;
  const isSameDayAvailable = allSameDayTiers.some(t => t.isAvailable && t.isCutoffPassed !== true);

  let state: ExpressBlockResolution['state'];
  if (isUberAvailable && isSameDayAvailable) {
    state = 'bothActive';
  } else if ((isUberAvailable && sameDayTier && !isSameDayAvailable) || (isUberAvailable  && sameDayTier === undefined)){
    state = 'sameDayDisabled';
  } else if (!isUberAvailable && isSameDayAvailable) {
    state = 'uberDisabled';
  } else if (!isUberAvailable && sameDayTier && !isSameDayAvailable) {
    state = sameDayTier.isCutoffPassed === true ? 'sameDayMissed' : 'hidden';
  } else {
    state = 'noneAvailable';
  }

  const isToggleVisible = isUberAvailable || isSameDayAvailable;

  const abDefault = state === 'bothActive' ? expressDefaultTierABTest.getVariant() : null;

  let uberOption: DeliveryOptionRow | null = null;
  if (uberTier || (sameDayTier && isUberAvailable === false)) {
    uberOption = {
      method: 'uber_2h',
      label: 'uber_2h_label',
      sublabel: '',
      priceEurCents: uberTier ? Math.round(uberTier.fee * 100) : 0,
      isEnabled: isUberAvailable,
      disabledReason: isUberAvailable ? null : 'uber_disabled_area',
      isDefault: abDefault === 'uber_2h' || (state === 'sameDayDisabled') || (state === 'uberDisabled' ? false : false),
    };
  }

  let sameDayOption: DeliveryOptionRow | null = null;
  // Build the same-day row when Urbify covers it (sameDayTier) OR when only Uber covers
  // the ZIP — in the Uber-only case we still surface a greyed "out of area" same-day row.
  if (sameDayTier || (uberTier && !sameDayTier)) {
    sameDayOption = {
      method: 'same_day',
      label: 'same_day_label',
      sublabel: 'same_day_sublabel_template',
      priceEurCents: sameDayTier ? Math.round(sameDayTier.fee * 100) : 0,
      isEnabled: isSameDayAvailable,
      disabledReason: isSameDayAvailable
        ? null
        : sameDayTier ? 'same_day_disabled_cutoff' : 'same_day_disabled_area',
      isDefault: abDefault === 'same_day',
    };
  }

  if (uberOption) {
    if (state === 'bothActive') {
      uberOption.isDefault = abDefault === 'uber_2h';
    } else if (state === 'sameDayDisabled') {
      uberOption.isDefault = true;
    } else if (state === 'uberDisabled') {
      uberOption.isDefault = false;
    }
  }
  if (sameDayOption) {
    if (state === 'bothActive') {
      sameDayOption.isDefault = abDefault === 'same_day';
    } else if (state === 'uberDisabled') {
      sameDayOption.isDefault = true;
    } else if (state === 'sameDayDisabled') {
      sameDayOption.isDefault = false;
    }
  }

  let countdown: ExpressBlockResolution['countdown'] = null;
  if (sameDayTier?.cutoffTime) {
    const seconds = getSecondsUntilCutoff(sameDayTier.cutoffTime);
    countdown = {
      cutOffTime: sameDayTier.cutoffTime,
      isPassed: sameDayTier.isCutoffPassed === true,
      countdownSeconds: seconds,
    };
    if (seconds === null && !sameDayTier.isCutoffPassed) {
      countdown = null;
    }
  }

  const fallbackStates: string[] = ['hidden', 'noneAvailable', 'sameDayMissed'];
  const isFallbackState = fallbackStates.includes(state);
  const fallbackMessage = state === 'sameDayMissed'
    ? 'same_day_missed_title'
    : isFallbackState
      ? 'express_delivery_not_available_title'
      : null;

  return {
    state,
    isToggleVisible,
    uberOption,
    sameDayOption,
    countdown,
    fallbackMessage,
  };
};
